import { Injectable, inject } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError, ResolveStart, ResolveEnd, RouteConfigLoadStart, RouteConfigLoadEnd } from '@angular/router';
import { LoadingController } from '@ionic/angular';
import { RESOLVER_LOADER_CONFIG, DEFAULT_RESOLVER_LOADER_CONFIG, ResolverLoaderConfig } from '@constants/resolver-loader.config';

/** Config interna con numeri garantiti e loading opzionale */
type InternalConfig = Required<Omit<ResolverLoaderConfig, 'loading'>> & { loading?: NonNullable<ResolverLoaderConfig['loading']> };

@Injectable({ providedIn: 'root' })
export class ResolverLoaderService {
	private router = inject(Router);
	private loadingCtrl = inject(LoadingController);

	private cfg: InternalConfig;

	// contatori separati
	private pendingNav = 0;
	private pendingHttp = 0;

	private navId: number | null = null;
	private targetUrl: string | null = null;
	private inNavigation = false;

	private presenting?: Promise<void>; // mutex per present
	private loading?: HTMLIonLoadingElement;
	private presentTimer?: any;
	private shownAt = 0;
	private hardStopTimer?: any;




	constructor() {
		const userCfg = inject(RESOLVER_LOADER_CONFIG, { optional: true }) ?? {};

		this.cfg = {
			...DEFAULT_RESOLVER_LOADER_CONFIG,
			...userCfg,
			loading: userCfg.loading,
		};

		// --- LISTENER ROUTER (come prima, ma usa pendingNav) ---
		this.router.events.subscribe(async ev => {
			if (ev instanceof NavigationStart) {
				// reset della sola navigazione
				this.navId = ev.id;
				this.targetUrl = ev.url;
				this.inNavigation = true;
				this.pendingNav = 0;
				this.clearTimers();
				// non chiudo lo spinner se ci sono http in corso
				if (this.totalPending() === 0) {
					await this.safeDismiss();
				}
			}

			if (ev instanceof ResolveStart && this.isCurrentNav(ev.id) && this.matches(ev.url)) {
				this.onNavStart();
			}

			if (this.cfg.includeLazyLoad && ev instanceof RouteConfigLoadStart) {
				if (this.inNavigation && this.targetUrl && this.matches(this.targetUrl)) {
					this.onNavStart();
				}
			}

			if (ev instanceof ResolveEnd && this.isCurrentNav(ev.id)) this.onNavEnd();
			if (this.cfg.includeLazyLoad && ev instanceof RouteConfigLoadEnd) this.onNavEnd();

			if (ev instanceof NavigationEnd || ev instanceof NavigationCancel || ev instanceof NavigationError) {
				this.inNavigation = false;
				this.navId = null;
				this.targetUrl = null;
				this.pendingNav = 0;
				this.clearTimers();
				// chiudo solo se non ci sono http pendenti
				if (this.totalPending() === 0) {
					await this.safeDismiss();
				}
			}
		});
	}

	// ------- API PUBBLICA PER L'INTERCEPTOR -------
	/** Inizia una operazione HTTP che richiede lo spinner */
	beginHttp(message?: string) {
		this.pendingHttp++;
		this.maybePresent(message);
	}

	/** Termina una operazione HTTP che richiede lo spinner */
	endHttp() {
		if (this.pendingHttp > 0) this.pendingHttp--;
		if (this.totalPending() === 0) {
			this.clearTimers();
			void this.safeDismiss();
		}
	}
	// ----------------------------------------------

	private totalPending() { return this.pendingNav + this.pendingHttp; }
	private isCurrentNav(id?: number | null) { return !!this.navId && this.navId === id; }
	private matches(url: string): boolean { try { return this.cfg.match(url); } catch { return true; } }

	private onNavStart() {
		this.pendingNav++;
		this.maybePresent();
	}

	private onNavEnd() {
		if (this.pendingNav > 0) this.pendingNav--;
		if (this.totalPending() === 0) {
			this.clearTimers();
			void this.safeDismiss();
		}
	}

	private async openLoading(customMessage?: string) {
		// evita più create/present concorrenti
		if (this.loading) return;
		if (this.presenting) return;

		this.presenting = (async () => {
			const base = {
				message: customMessage ?? this.cfg.message,
				spinner: this.cfg.spinner,
				backdropDismiss: false as const
			};

			const loading = await this.loadingCtrl.create({
				...base,
				...(this.cfg.loading ?? {})
			});

			this.loading = loading;
			this.shownAt = Date.now();
			await loading.present();

			// failsafe
			this.hardStopTimer = setTimeout(() => this.forceDismiss(), this.cfg.hardStopMs);
		})();

		try {
			await this.presenting;
		} finally {
			this.presenting = undefined;
		}
	}



	private maybePresent(customMessage?: string) {
		if (this.totalPending() === 1) { // transizione 0 → 1
			this.clearTimers();
			this.presentTimer = setTimeout(() => {
				if (this.totalPending() === 0) return;   // nel frattempo è finito tutto
				void this.openLoading(customMessage);    // 👈 serializza present
			}, this.cfg.delayMs);
		}
	}

	private clearTimers() {
		if (this.presentTimer) { clearTimeout(this.presentTimer); this.presentTimer = undefined; }
		if (this.hardStopTimer) { clearTimeout(this.hardStopTimer); this.hardStopTimer = undefined; }
	}

	private async safeDismiss() {
		// se present() è in corso, aspetta che finisca (evita dismiss prima del present)
		if (this.presenting) {
			try { await this.presenting; } catch { /* ignore */ }
		}

		const loading = this.loading;
		if (!loading) return;

		// reset subito per evitare doppie dismiss
		this.loading = undefined;

		const elapsed = Date.now() - this.shownAt;
		const wait = Math.max(0, this.cfg.minDurationMs - elapsed);
		await new Promise(r => setTimeout(r, wait));

		try {
			await loading.dismiss();
		} catch {
			// overlay già chiuso: ignora
		}
	}

	private async forceDismiss() {
		this.pendingNav = 0;
		this.pendingHttp = 0;
		this.clearTimers();
		await this.safeDismiss();
	}
}
